import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  DeleteConfirmDialog,
  AddOns,
  AllergenDialog,
  DetailsDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteStandardFoodById,
  clearStandardFoodMessages,
  updateStandardFoodById,
  fetchAllStandardFoods,
} from "@/redux/slice/menu-manage-slice/standard-food-slice";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function StandardFoodListTable({ standardFoods }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.standardFood
  );
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [itemsDialog, setItemsDialog] = useState(null);
  const [addOnsDialog, setAddOnsDialog] = useState(false);
  const [allergenDialog, setAllergenDialog] = useState(false);
  const [hyderabadiDetails, setHyderabadiDetails] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setItemsDialog(null);
  };
  const closeAddOns = () => {
    setAddOnsDialog(false);
    setItemsDialog(null);
  };
  const closeAllergen = () => {
    setAllergenDialog(false);
    setItemsDialog(null);
  };
  const colseHyderabadi = () => {
    setHyderabadiDetails(false);
    setItemsDialog(null);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearStandardFoodMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateStandardFoodById({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateStandardFoodById.fulfilled.match(res)) {
        toast.success(`Standard food archived successfully!`);
      } else if (updateStandardFoodById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive standard food.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearStandardFoodMessages());
      const res = await dispatch(deleteStandardFoodById(id));
      if (deleteStandardFoodById.fulfilled.match(res)) {
        toast.success(`Standard food deleted successfully!`);
      } else if (deleteStandardFoodById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete standard food.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemsDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("standard-foods", id);
      if (res.status === "success") {
        toast.success(res.message || "Standard food status updated successfully");
        dispatch(fetchAllStandardFoods(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle standard food status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Item No</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Add-ons</TableHead>
            <TableHead>Allergen</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {standardFoods && standardFoods?.length > 0 ? (
            standardFoods?.map((item, index) => (
              <TableRow key={index}>
                <TableCell className="font-medium">
                  {item?.item_no || "N/A"}
                </TableCell>
                <TableCell>{item?.name}</TableCell>
                <TableCell>{item?.category_name || "N/A"}</TableCell>
                <TableCell>${item?.price || "0"}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-1 cursor-pointer">
                    {item?.add_ons_count}
                    <Eye
                      onClick={() => {
                        setAddOnsDialog(true);
                        setItemsDialog(item);
                      }}
                      size={18}
                    />
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1 cursor-pointer">
                    {item?.allergens_count}
                    <Eye
                      onClick={() => {
                        setAllergenDialog(true);
                        setItemsDialog(item);
                      }}
                      size={18}
                    />
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {item?.status === "active" ? (
                      <>
                        <span className="text-cgreen text-base">Active</span>
                        <Switch
                          checked={true}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "inactive" ? (
                      <>
                        <span className="text-cred text-base">Inactive</span>
                        <Switch
                          checked={false}
                          onCheckedChange={() => handleToggleStatus(item.id)}
                        />
                      </>
                    ) : item?.status === "archived" ? (
                      <span className="text-cred text-base">Archived</span>
                    ) : (
                      <span className="text-cred text-base">Draft</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right justify-end flex gap-2 items-center">
                  <div className="flex items-center gap-2 h-8">
                    <span>
                      <Info
                        onClick={() => {
                          setHyderabadiDetails(true);
                          setItemsDialog(item);
                        }}
                        className="text-cwhite3 cursor-pointer"
                        size={18}
                      />
                    </span>

                    {hasPermission("back_office.menu_management.update_standard_food") && item?.status !== "archived" && (
                      <Link
                        to={"/backoffice/menu/standard-foods/update"}
                        state={item}
                        className="cursor-pointer"
                      >
                        <PenLine className="text-cyellow" size={18} />
                      </Link>
                    )}
                      {hasPermission("back_office.menu_management.delete_standard_food") && item?.status !== "archived" && (
                        <span>
                          {item?.status === "draft" ? (
                            <Trash2
                              onClick={() => {
                                setItemsDialog(item);
                                setDeleteDialog(true);
                              }}
                              className="text-cred cursor-pointer"
                              size={18}
                            />
                          ) : (
                            <Archive
                              onClick={() => {
                                setArchiveActionType("archive");
                                setItemsDialog(item);
                                setArchiveDialog(true);
                              }}
                              className="text-cred cursor-pointer"
                              size={18}
                            />
                          )}
                        </span>
                      )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemsDialog.id, itemsDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Standard Food?" : "Restore Standard Food?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemsDialog?.name}"?`
            : `Are you sure you want to restore "${itemsDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemsDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemsDialog.id);
        }}
        title="Delete Draft Standard Food?"
        description={`Are you sure you want to delete the draft standard food "${itemsDialog?.name}"? This action cannot be undone.`}
      />

      <AllergenDialog
        items={itemsDialog}
        isOpen={allergenDialog}
        onClose={closeAllergen}
      />
      <AddOns items={itemsDialog} isOpen={addOnsDialog} onClose={closeAddOns} />
      <DetailsDialog
        items={itemsDialog}
        isOpen={hyderabadiDetails}
        onClose={colseHyderabadi}
      />
    </>
  );
}

export default StandardFoodListTable;
